import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  Checkbox,
  Box,
  Typography,
  Button,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { ExchangeOffice, useExchangeOfficeOptionsByDO, useAvailableSendingPpos } from '../hooks';

interface RemunerationGroupsTableProps {
  data: ExchangeOffice[];
  receivingPpo?: string;
  exchangeOfficeOptions: { id: string; label: string }[];
  isExchangeOfficeLoading: boolean;
  notSubjToSampling?: boolean;
  onQuarterChange: (exchangeOffice: ExchangeOffice, quarter: keyof Pick<ExchangeOffice, 'quarter1' | 'quarter2' | 'quarter3' | 'quarter4'>, value: boolean) => void;
  onPpoChange: (exchangeOffice: ExchangeOffice, newPpo: string) => void;
  onExchangeOfficeChange: (exchangeOffice: ExchangeOffice, newExchangeOffice: string) => void;
  onAddNewRow: (newRow: ExchangeOffice) => void;
  onRemoveRow: (exchangeOffice: ExchangeOffice) => void;
  onSave: (data: any) => void;
  onNotSubjToSamplingChange: (value: boolean) => void;
  isSaving?: boolean;
}

export function RemunerationGroupsTable({
  data,
  receivingPpo,
  exchangeOfficeOptions,
  isExchangeOfficeLoading,
  notSubjToSampling = false,
  onQuarterChange,
  onPpoChange,
  onExchangeOfficeChange,
  onAddNewRow,
  onRemoveRow,
  onSave,
  onNotSubjToSamplingChange,
  isSaving = false,
}: RemunerationGroupsTableProps) {
  const [newRow, setNewRow] = useState<Partial<ExchangeOffice>>({
    ppo: '',
    exchangeOffice: '',
    quarter1: true,
    quarter2: true,
    quarter3: true,
    quarter4: true,
  });

  // Fetch exchange office options filtered by selected DO
  const {
    data: filteredExchangeOfficeOptions,
    isLoading: isFilteredExchangeOfficeLoading,
  } = useExchangeOfficeOptionsByDO(newRow.ppo);

  // Fetch available sending DOs for UPU based on selected receiving PPO
  const { data: sendingPpoOptions = [] } = useAvailableSendingPpos(receivingPpo);

  const handleNewRowChange = (field: keyof ExchangeOffice, value: any) => {
    setNewRow(prev => {
      const updated = { ...prev, [field]: value };
      // Reset exchange office when DO changes
      if (field === 'ppo') {
        updated.exchangeOffice = '';
      }
      return updated;
    });
  };

  const handleAddNewRow = () => {
    if (newRow.ppo && newRow.exchangeOffice) {
      const newExchangeOffice: ExchangeOffice = {
        ppo: newRow.ppo!,
        exchangeOffice: newRow.exchangeOffice!,
        quarter1: true,
        quarter2: true,
        quarter3: true,
        quarter4: true,
      };
      onAddNewRow(newExchangeOffice);
      setNewRow(prev => ({
        ...prev,
        exchangeOffice: '',
      }));
    }
  };

  const canAddNewRow = newRow.ppo && newRow.exchangeOffice;

  return (
    <Box>
      {/* Not Subject to Sampling Checkbox */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <Checkbox
          checked={notSubjToSampling}
          onChange={(e) => onNotSubjToSamplingChange(e.target.checked)}
          sx={{
            color: '#E1E8ED',
            '&.Mui-checked': {
              color: '#1976d2',
            },
            mr: 1,
          }}
        />
        <Typography
          variant="body2"
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: '#063142',
          }}
        >
          This Group is not subject to sampling (Non-EU-ETOE to DC)
        </Typography>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #E1E8ED' }}>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#F8F9FA' }}>
            <TableCell sx={{ fontWeight: 600, color: '#063142' }}>DO</TableCell>
            <TableCell sx={{ fontWeight: 600, color: '#063142' }}>Exchange Office</TableCell>
            <TableCell sx={{ fontWeight: 600, color: '#063142', textAlign: 'center' }}>Q1</TableCell>
            <TableCell sx={{ fontWeight: 600, color: '#063142', textAlign: 'center' }}>Q2</TableCell>
            <TableCell sx={{ fontWeight: 600, color: '#063142', textAlign: 'center' }}>Q3</TableCell>
            <TableCell sx={{ fontWeight: 600, color: '#063142', textAlign: 'center' }}>Q4</TableCell>
            <TableCell sx={{ fontWeight: 600, color: '#063142', textAlign: 'center' }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((exchangeOffice, index) => (
            <TableRow key={`${exchangeOffice.ppo}-${exchangeOffice.exchangeOffice}-${index}`} hover>
              <TableCell sx={{ color: '#063142' }}>{exchangeOffice.ppo}</TableCell>
              <TableCell sx={{ color: '#063142' }}>{exchangeOffice.exchangeOffice}</TableCell>
              
              {/* Quarter 1 Checkbox */}
              <TableCell sx={{ textAlign: 'center' }}>
                <Checkbox
                  checked={notSubjToSampling ? true : exchangeOffice.quarter1}
                  disabled={notSubjToSampling}
                  onChange={(e) => onQuarterChange(exchangeOffice, 'quarter1', e.target.checked)}
                  sx={{
                    color: '#E1E8ED',
                    '&.Mui-checked': {
                      color: '#1976d2',
                    },
                    '&.Mui-disabled': {
                      color: '#bdbdbd',
                    },
                  }}
                />
              </TableCell>
              
              {/* Quarter 2 Checkbox */}
              <TableCell sx={{ textAlign: 'center' }}>
                <Checkbox
                  checked={notSubjToSampling ? true : exchangeOffice.quarter2}
                  disabled={notSubjToSampling}
                  onChange={(e) => onQuarterChange(exchangeOffice, 'quarter2', e.target.checked)}
                  sx={{
                    color: '#E1E8ED',
                    '&.Mui-checked': {
                      color: '#1976d2',
                    },
                    '&.Mui-disabled': {
                      color: '#bdbdbd',
                    },
                  }}
                />
              </TableCell>
              
              {/* Quarter 3 Checkbox */}
              <TableCell sx={{ textAlign: 'center' }}>
                <Checkbox
                  checked={notSubjToSampling ? true : exchangeOffice.quarter3}
                  disabled={notSubjToSampling}
                  onChange={(e) => onQuarterChange(exchangeOffice, 'quarter3', e.target.checked)}
                  sx={{
                    color: '#E1E8ED',
                    '&.Mui-checked': {
                      color: '#1976d2',
                    },
                    '&.Mui-disabled': {
                      color: '#bdbdbd',
                    },
                  }}
                />
              </TableCell>
              
              {/* Quarter 4 Checkbox */}
              <TableCell sx={{ textAlign: 'center' }}>
                <Checkbox
                  checked={notSubjToSampling ? true : exchangeOffice.quarter4}
                  disabled={notSubjToSampling}
                  onChange={(e) => onQuarterChange(exchangeOffice, 'quarter4', e.target.checked)}
                  sx={{
                    color: '#E1E8ED',
                    '&.Mui-checked': {
                      color: '#1976d2',
                    },
                    '&.Mui-disabled': {
                      color: '#bdbdbd',
                    },
                  }}
                />
              </TableCell>
              
              {/* Remove Button */}
              <TableCell sx={{ textAlign: 'center' }}>
                <IconButton
                  onClick={() => onRemoveRow(exchangeOffice)}
                  size="small"
                  sx={{
                    color: '#d32f2f',
                    '&:hover': {
                      backgroundColor: '#ffebee',
                      color: '#c62828',
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          
        </TableBody>
      </Table>
      </TableContainer>

      {/* Add New Row Section */}
      <Box sx={{ mt: 3, p: 2, border: '1px solid #E1E8ED', borderRadius: 1, backgroundColor: '#F8F9FA' }}>
        <Typography variant="h6" sx={{ mb: 2, fontSize: '16px', fontWeight: 600, color: '#063142' }}>
          Add New Exchange Office
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: 200 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#063142', mb: 1 }}>
              DO: *
            </Typography>
            <Select
              size="small"
              value={newRow.ppo || ''}
              onChange={(e) => handleNewRowChange('ppo', e.target.value)}
              displayEmpty
              fullWidth
              sx={{ 
                backgroundColor: 'white',
                '& .MuiSelect-select': {
                  fontSize: '14px',
                }
              }}
            >
              <MenuItem value="">
                <Typography variant="body2" sx={{ color: '#666' }}>Select DO</Typography>
              </MenuItem>
              {sendingPpoOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  <Typography variant="body2">{option.label}</Typography>
                </MenuItem>
              ))}
            </Select>
          </Box>
          
          <Box sx={{ minWidth: 250 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#063142', mb: 1 }}>
              Exchange Office: *
            </Typography>
            <Select
              size="small"
              value={newRow.exchangeOffice || ''}
              onChange={(e) => handleNewRowChange('exchangeOffice', e.target.value)}
              displayEmpty
              disabled={!newRow.ppo || isFilteredExchangeOfficeLoading}
              fullWidth
              sx={{ 
                backgroundColor: 'white',
                '& .MuiSelect-select': {
                  fontSize: '14px',
                }
              }}
            >
              <MenuItem value="">
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {!newRow.ppo 
                    ? 'Select DO first' 
                    : isFilteredExchangeOfficeLoading 
                      ? 'Loading...' 
                      : 'Select Exchange Office'
                  }
                </Typography>
              </MenuItem>
              {filteredExchangeOfficeOptions?.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  <Typography variant="body2">{option.label}</Typography>
                </MenuItem>
              ))}
            </Select>
          </Box>
          
          <Button
            variant="contained"
            onClick={handleAddNewRow}
            disabled={!canAddNewRow}
            sx={{
              px: 3,
              py: 1,
              fontSize: '14px',
              fontWeight: 600,
              backgroundColor: canAddNewRow ? '#1976d2' : '#ccc',
              '&:hover': {
                backgroundColor: canAddNewRow ? '#1565c0' : '#ccc',
              }
            }}
          >
            Add 
          </Button>
        </Box>
      </Box>
      
      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          size="large"
          onClick={() => onSave(data)}
          disabled={isSaving}
          sx={{
            px: 4,
            py: 1.5,
            fontSize: '16px',
            fontWeight: 600,
            backgroundColor: '#1976d2',
            '&:hover': {
              backgroundColor: '#1565c0',
            },
            '&:disabled': {
              backgroundColor: '#ccc',
            }
          }}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
}
